import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { ChevronRight } from 'lucide-react';
import { formatCents, type Property } from '@vuokramani/shared';
import { pageStyles as s } from '../../components/page.tsx';
import { formatPercentFromBp, propertyTitle } from '../../lib/properties.ts';

/**
 * Asuntolistan rivi: vasemmalla nimi ja osoite, oikealla vuokra ja vuokralainen
 * (tai "Vapaa" / "Päättäminen kesken", jos vuokralainen on muuttanut pois mutta vuokrasuhde on
 * sulkematta). Omistusosuus näytetään vain, jos se ei ole koko asunto.
 */
export function PropertyRow({ property, to }: { property: Property; to: string }) {
  const { t, i18n } = useTranslation();
  const owner = property.myRole === 'owner';
  const lease = property.currentLease;
  const partial = owner && property.myOwnershipBp !== null && property.myOwnershipBp < 10000;
  const meta = [
    property.name ? property.streetAddress : null,
    `${property.postalCode} ${property.city}`,
    property.roomLayout,
    property.areaM2 ? `${property.areaM2.toLocaleString(i18n.language)} m²` : null,
    partial
      ? t('properties.ownershipShort', {
          percent: formatPercentFromBp(property.myOwnershipBp!, i18n.language),
        })
      : null,
  ]
    .filter(Boolean)
    .join(', ');

  const names = (list: string[] | undefined) =>
    list?.length ? new Intl.ListFormat(i18n.language, { type: 'conjunction' }).format(list) : null;
  const tenants = names(lease?.tenantNames);
  const movedOut = property.movedOutLease;

  return (
    <Link to={to} className={s.propertyRow}>
      <span className={s.propertyText}>
        <span className={s.propertyName}>{propertyTitle(property)}</span>
        <span className={s.propertyMeta}>{meta}</span>
      </span>
      {owner && !property.archivedAt && (
        <span className={s.propertyFigure}>
          {lease ? (
            <>
              <span className="num">
                {t('properties.perMonth', { amount: formatCents(lease.rentCents, i18n.language) })}
              </span>
              {tenants && <span className={s.propertyMeta}>{tenants}</span>}
            </>
          ) : movedOut ? (
            <>
              <span className={`${s.tag} ${s.tagWarn}`}>{t('properties.closingPending')}</span>
              {names(movedOut.tenantNames) && (
                <span className={s.propertyMeta}>{names(movedOut.tenantNames)}</span>
              )}
            </>
          ) : (
            <span className={`${s.tag} ${s.tagWarn}`}>{t('properties.vacant')}</span>
          )}
        </span>
      )}
      {property.myRole === 'tenant' && (
        <span className={`${s.tag} ${s.tagAccent}`}>{t('properties.tenantOf')}</span>
      )}
      <ChevronRight size={18} aria-hidden="true" className={s.chevron} />
    </Link>
  );
}
